import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { timesheetStatusOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppRateInput from '@nxsys/components/AppRateInput';
import { useGetTimesheetImports } from '@nxsys/hooks/api/timesheets/useTimesheetsApi';
import { hasFilterValue } from '@nxsys/utils/filterForm';

type FilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: GetListTimesheetsParams) => void;
  isPendingApproval?: boolean;
  showImportFilter?: boolean;
};

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
  isPendingApproval = false,
  showImportFilter = false,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();

  const {
    timesheetsData: { queryParams },
  } = useSelector((state: AppState) => state.timesheetsStore);

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const { isEmployee, isAgency, isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  // NX-5237 (#9): import files available to filter by (this tenant + tax year).
  // Fetched lazily when the modal is open and the import filter is enabled.
  const {
    value: importsValue,
    loading: importsLoading,
    run: runGetImports,
  } = useGetTimesheetImports();

  // Derive the import-fetch scope from the user's identity — the SAME rule the
  // list uses (Timesheets.tsx) — NOT from timesheetsData.queryParams.
  // queryParams is only populated after the list has loaded and persists across
  // routes, so reading companyRefs/noFilterByApprover from it can send a null
  // or stale scope and make the dropdown's options diverge from the list.
  const importScope = useMemo(
    () =>
      isBureauEmployer
        ? { companyRefs: user?.companyUserIds || [], noFilterByApprover: true }
        : {},
    [isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    if (showImportFilter && isOpen) {
      runGetImports({
        taxYear: queryParams?.taxYear,
        ...importScope,
      });
    }
    // runGetImports identity is unstable; exhaustive-deps is disabled in this repo.
  }, [showImportFilter, isOpen, queryParams?.taxYear, importScope]);

  const importOptions = useMemo(
    () =>
      (importsValue?.data ?? []).map((item) => ({
        value: item.importId,
        label: item.fileName
          ? `${item.fileName} (${item.count})`
          : `${item.importId} (${item.count})`,
      })),
    [importsValue],
  );

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params = {
      ...queryParams,
      startDate: values.weekEndDateFrom
        ? moment(values.weekEndDateFrom).format('MM/DD/YYYY')
        : undefined,
      endDate: values.weekEndDateTo
        ? moment(values.weekEndDateTo).format('MM/DD/YYYY')
        : undefined,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      batchFrom: values.batchFrom,
      batchTo: values.batchTo,
      timesheetId: values.timesheetId,
      approveStatus: values.approveStatus,
      ...(showImportFilter ? { importId: values.importId } : {}),
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const renderFilterContent = () => {
    if (isEmployee || isAgency) {
      return (
        <Form.Item name="approveStatus" label={messages['common.status']}>
          <AppSelect
            value={timesheetStatusOptions.find(
              (option) => option.value === watchValue?.approveStatus,
            )}
            options={timesheetStatusOptions}
            onChange={(value) => {
              form.setFieldsValue({
                approveStatus: value?.value,
              });
            }}
            placeholder={messages['form.selectStatus']}
            allowClear
          />
        </Form.Item>
      );
    }

    return (
      <>
        {/* NX-5775: Timesheet ID is filterable on every admin listing, including
            the standard /timesheets listing. */}
        <Form.Item name="timesheetId" label={messages['timesheet.timesheetId']}>
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>

        <Typography.Text>{messages['timesheet.weekEndDate']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="weekEndDateFrom">
            <AppDatePicker
              placeholder={messages['common.fromDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  weekEndDateFrom: date,
                });
              }}
              format="DD/MM/YYYY"
              maxDate={watchValue?.weekEndDateTo}
            />
          </Form.Item>
          <Form.Item name="weekEndDateTo">
            <AppDatePicker
              placeholder={messages['common.toDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  weekEndDateTo: date,
                });
              }}
              format="DD/MM/YYYY"
              minDate={watchValue?.weekEndDateFrom}
            />
          </Form.Item>
        </div>

        {/* NX-5775: `!isAwaitingInvoice || (!isPendingApproval && …)` rendered
            `true` (nothing) on the standard listing. Pending approval is already
            grouped by tax period, so the filter is only hidden there. */}
        {!isPendingApproval && (
          <>
            <Typography.Text>{messages['common.taxPeriod']}</Typography.Text>
            <div className="grid grid-cols-2  gap-x-4 mt-2">
              <Form.Item name="taxPeriodFrom">
                <AppRateInput placeholder={messages['common.from']} />
              </Form.Item>
              <Form.Item name="taxPeriodTo">
                <AppRateInput placeholder={messages['common.to']} />
              </Form.Item>
            </div>
          </>
        )}

        <Typography.Text>{messages['timesheet.batch']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="batchFrom">
            <AppRateInput placeholder={messages['common.from']} />
          </Form.Item>
          <Form.Item name="batchTo">
            <AppRateInput placeholder={messages['common.to']} />
          </Form.Item>
        </div>

        {showImportFilter && (
          <>
            <Typography.Text>
              {messages['timesheet.selectImport']}
            </Typography.Text>
            <div className="mt-2">
              <Form.Item name="importId">
                <AppSelect
                  value={importOptions.find(
                    (option) => option.value === watchValue?.importId,
                  )}
                  options={importOptions}
                  onChange={(value) => {
                    form.setFieldsValue({
                      importId: value?.value,
                    });
                  }}
                  placeholder={messages['timesheet.selectImport']}
                  loading={importsLoading}
                  allowClear
                />
              </Form.Item>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {renderFilterContent()}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
